import 'react-circular-progressbar/dist/styles.css';
import styles from './style.module.css';

import moment from 'moment';
import { useCallback, useEffect, useState } from 'react';
import { useLocation, useNavigate, useParams } from 'react-router-dom';

import { ButtonBack } from '../../components/ButtonBack';
import { Card } from '../../components/Card';
import { Notify } from '../../components/Notify';
import { ModalHasAnswered } from './components/ModalHasAnsered';

import { ACTION_DETAILS, HOME, KNOWLEDGE_TRAIL } from '../../constants/Routes';
import { useAuth } from '../../hooks/useAuth';
import { api, getAPIURL } from '../../server/api';
import Skeleton from 'react-loading-skeleton';
import { IAttempt } from '../../interfaces/IAction';
import { IAnswer } from '../../interfaces/IQuizAnswer';
import { ModalHasSucced } from './components/ModalHasSucced';
import { ModalHasFailed } from './components/ModalHasFailed';
import { ModalNoMoreAttempts } from './components/ModalNoMoreAttempts';
import { ModalHasEmptyAnswers } from './components/ModalHasEmptyAnswers';
import { ModalPerformanceSucced } from './components/ModalPerformanceSucced';
import { ModalPerformanceFailed } from './components/ModalPerformanceFailed';

export function Quiz() {
  const { player, client } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const locationState = location.state as {
    returnPath?: string;
    returnState?: Record<string, unknown>;
  } | null;
  const returnPath = locationState?.returnPath;
  const returnState = locationState?.returnState;

  const { actionId } = useParams();
  const [attempt, setAttempt] = useState<IAttempt>();
  const [chosenAnswer, setChosenAnswer] = useState<string>('');
  const [answers, setAnswers] = useState<IAnswer[]>([]);
  const [startQuiz, setStartQuiz] = useState<boolean>(false);
  const [finishedQuiz, setFinishedQuiz] = useState<boolean>(false);
  const [modalHasAnsweredIsOpen, setModalHasAnsweredIsOpen] =
    useState<boolean>(false);
  const [modalHasSucced, setModalHasSucced] = useState<boolean>(false);
  const [modalHasFailed, setModalHasFailed] = useState<boolean>(false);
  const [modalNoMoreAttempts, setModalNoMoreAttempts] =
    useState<boolean>(false);
  const [modalHasEmptyAnswers, setModalHasEmptyAnswers] =
    useState<boolean>(false);
  const [emptyAnswersCount, setEmptyAnswersCount] = useState<number>(0);
  const [modalPerformanceSucced, setModalPerformanceSucced] =
    useState<boolean>(false);
  const [modalPerformanceFailed, setModalPerformanceFailed] =
    useState<boolean>(false);
  const [page, setPage] = useState<number>(0);
  const [seconds, setSeconds] = useState<number>(0);
  const [countDownVisible, setCountDownVisible] = useState<boolean>(false);
  const [totalPoints, setTotalPoints] = useState<number>(0);
  const [accuracyPercentage, setAccuracyPercentage] = useState<number>(0);
  const [amountOfCorrectAnswers, setAmountOfCorrectAnswers] =
    useState<number>(0);
  const [quizPoints, setQuizPoints] = useState<number>(0);
  const [nOfAttemps, setNOfAttempts] = useState<number>(0);
  const [amountOfRetries, setAmountOfRetries] = useState<number>();
  const [alreadyScoredPreviously, setAlreadyScoredPreviously] =
    useState<boolean>(false);
  const [previousScoredPoints, setPreviousScoredPoints] = useState<number>(0);
  const [previousScoredAt, setPreviousScoredAt] = useState<number>();
  const [linkedToLandingBusiness, setLinkedToLandingBusiness] =
    useState<boolean>(false);
  const [minimumScore, setMinimumScore] = useState<number>();
  const [knowledgeTrailId, setKnowledgeTrailId] = useState<string>();

  const isKnowledgeTrail =
    !!knowledgeTrailId || returnPath?.startsWith(KNOWLEDGE_TRAIL);

  useEffect(() => {
    startAnswer();
    getMinimumScore();
  }, []);

  useEffect(() => {
    if (countDownVisible) {
      const intervalId = setInterval(() => {
        setSeconds((tempoAnterior) => tempoAnterior - 1);
        if (seconds === 1) {
          handleFinish(answers, true);
        }
      }, 1000);

      return () => clearInterval(intervalId);
    }
  }, [seconds]);

  const startAnswer = useCallback(async () => {
    const parameters = {
      playerId: player.id,
      actionId: actionId,
    };
    try {
      const { data } = await api.post('/actions/quiz/start', parameters);
      setAttempt(data.body);
      setStartQuiz(true);
      if (data.body.minutesTimer) {
        setCountDownVisible(true);
        setSeconds(data.body.minutesTimer * 60);
      }
      setNOfAttempts(data.body.nOfAttempts);
      setAmountOfRetries(data.body.amountOfRetries);
      setLinkedToLandingBusiness(data.body.linkedToLandingBusiness);
      const emptyAnswers = data.body.questions.map((question) => ({
        questionIndex: question.questionIndex,
        answer: '',
      }));
      setAnswers(emptyAnswers);
    } catch (error) {
      if (error.response.status === 403) setModalHasAnsweredIsOpen(true);
    }
  }, [actionId, player]);

  const getMinimumScore = useCallback(async () => {
    try {
      const parameters = {
        searchFunctionality: 'getAction',
        userType: 'player',
        actionId,
        clientId: client.id,
        hostname: getAPIURL(),
      };
      const { data } = await api.post('/search', parameters);
      if (data.success) {
        setMinimumScore(data.data.action?.quizDetails?.minimumScore);
        setKnowledgeTrailId(data.data.action?.knowledgeTrailId);
      }
    } catch (error) {
      console.log('getMinimumScore error:', error);
    }
  }, [actionId, client]);

  const sendAwsers = async (updatedAnswers: IAnswer[]) => {
    const parameters = {
      attemptId: attempt?.attemptId,
      actionId: actionId,
      playerId: player.id,
      answers: updatedAnswers,
    };
    try {
      const { data } = await api.post('/actions/quiz/finish', parameters);
      setTotalPoints(data.body.totalPointsScored);
      setAccuracyPercentage(
        parseFloat(data.body.accuracyPercentage?.toFixed(2))
      );
      setAmountOfCorrectAnswers(data.body.amountOfCorrectAnswers);
      setQuizPoints(data.body.quizRewardPoints);
      setAlreadyScoredPreviously(!!data.body.alreadyScoredPreviously);
      setPreviousScoredPoints(data.body.previousScoredPoints ?? 0);
      setPreviousScoredAt(data.body.previousScoredAt);
      Notify(data.message, 'success');
      return {
        totalPointsScored: data.body.totalPointsScored,
        scored: data.body.scored,
      };
    } catch (error) {
      Notify(error.message, 'error');
    }
  };

  const handleSubmit = (): IAnswer[] => {
    const newAnswers = answers.map((question) => {
      return question.questionIndex === page
        ? { ...question, answer: chosenAnswer }
        : question;
    });
    setAnswers(newAnswers);
    return newAnswers;
  };

  const goToQuestion = (targetIndex: number) => {
    if (targetIndex < 0 || targetIndex >= answers.length) return;
    if (chosenAnswer !== '') {
      setAnswers((prev) =>
        prev.map((q) =>
          q.questionIndex === page ? { ...q, answer: chosenAnswer } : q
        )
      );
    }
    setPage(targetIndex);
    const targetAnswer = answers.find((a) => a.questionIndex === targetIndex);
    setChosenAnswer(targetAnswer?.answer ? targetAnswer.answer : '');
  };

  const handleFinish = async (
    updatedAnswers: IAnswer[],
    finishedTime: boolean = false
  ) => {
    const hasEmptyAnswer = updatedAnswers.some((item) => item.answer === '');
    if (!hasEmptyAnswer || finishedTime) {
      const result = await sendAwsers(updatedAnswers);
      const pointsEarned = result?.totalPointsScored;
      const resultScored = result?.scored;

      setFinishedQuiz(true);
      if (linkedToLandingBusiness) {
        if (resultScored === true) {
          setModalPerformanceSucced(true);
        } else {
          setModalPerformanceFailed(true);
        }
      } else {
        if (!isKnowledgeTrail) {
          if (pointsEarned > 0) {
            setModalHasSucced(true);
          }
          if (
            pointsEarned === 0 &&
            (!amountOfRetries || nOfAttemps < amountOfRetries)
          ) {
            setModalHasFailed(true);
          }
          if (pointsEarned === 0 && nOfAttemps === amountOfRetries) {
            setModalNoMoreAttempts(true);
          }
        }
      }
    } else {
      setEmptyAnswersCount(
        updatedAnswers.filter((item) => item.answer === '').length
      );
      setModalHasEmptyAnswers(true);
    }
  };

  const formatarTempo = (tempoEmSegundos) => {
    const minutos = Math.floor(tempoEmSegundos / 60);
    const segundos = tempoEmSegundos % 60;
    return `${minutos.toString().padStart(2, '0')}:${segundos.toString().padStart(2, '0')}`;
  };

  return (
    <>
      {!startQuiz && !finishedQuiz && (
        <div className="conteiner">
          <div className="container">
            <div className="row">
              <div className="my-3">
                <Skeleton height={50} />
              </div>
            </div>
            <div className="row">
              <div className="col-sm-12 col-md-10">
                <Skeleton height={50} />
                <div className="mt-3">
                  <Skeleton height={400} />
                </div>
              </div>
              <div className="col-sm-12 col-md-2">
                <Skeleton height={200} />
              </div>
            </div>
          </div>
        </div>
      )}

      {startQuiz && !finishedQuiz && (
        <div className="container">
          <div className={styles.quizTopBar}>
            <span className={styles.quizCounter}>
              Questão {page + 1} de {attempt?.questions.length}
            </span>
          </div>
          <div className={`${styles.quizProgressWrap} mb-3`}>
            <div
              className={`${styles.quizProgressFill} ${styles.quizProgressFillGradient}`}
              style={{
                width: `${((page + 1) / attempt.questions.length) * 100}%`,
              }}
            />
          </div>
          <div className="row g-3">
            <div className="col-sm-12 col-md-8">
              <Card
                style={{ padding: 0, overflow: 'hidden', marginBottom: 12 }}
              >
                <div className={styles.quizQuestionHeader}>
                  <span className={styles.quizQuestionIcon}>
                    <i className="material-icons-outlined">article</i>
                  </span>
                  <div>
                    <span className={styles.quizQuestionMeta}>
                      Questão {page + 1} de {attempt.questions.length}
                    </span>
                    <h2 className={styles.quizQuestionText}>
                      {attempt.questions[page].question}
                    </h2>
                  </div>
                </div>
                <div className={styles.quizQuestionBody}>
                  <p className={styles.quizSelectLabel}>
                    Selecione a resposta correta:
                  </p>
                  <div className={styles.quizOptions}>
                    {(['a', 'b', 'c', 'd'] as const).map((key) => {
                      const letter = key.toUpperCase();
                      const selected = chosenAnswer === letter;
                      return (
                        <button
                          key={key}
                          type="button"
                          className={`${styles.quizOption} ${
                            selected ? styles.quizOptionSelected : ''
                          }`}
                          onClick={() => setChosenAnswer(letter)}
                        >
                          <span className={styles.quizOptionLetter}>
                            {letter}
                          </span>
                          <span className={styles.quizOptionText}>
                            {attempt.questions[page]?.alternatives[key]}
                          </span>
                          <span className={styles.quizOptionRadio}>
                            {selected && (
                              <span className={styles.quizOptionDot} />
                            )}
                          </span>
                        </button>
                      );
                    })}
                  </div>
                </div>
              </Card>
              <div className={styles.quizNavRow}>
                <button
                  type="button"
                  className={`${styles.quizNavBack} ${
                    page === 0 ? styles.quizNavBackDisabled : ''
                  }`}
                  disabled={page === 0}
                  onClick={() => goToQuestion(page - 1)}
                >
                  <i className="material-icons-outlined">chevron_left</i>
                  Anterior
                </button>
                <button
                  type="button"
                  className={styles.quizNavNext}
                  onClick={async () => {
                    const updatedAnswers = handleSubmit();
                    if (
                      page === attempt.questions?.length - 1 ||
                      answers.filter((item) => item.answer === '').length === 0
                    ) {
                      handleFinish(updatedAnswers);
                    } else {
                      setPage(page + 1);
                      setChosenAnswer(
                        answers[page + 1].answer ? answers[page + 1].answer : ''
                      );
                    }
                  }}
                >
                  {page === attempt.questions?.length - 1 ||
                  answers.filter((item) => item.answer === '').length === 0
                    ? 'Finalizar Quiz'
                    : 'Próxima questão'}
                  <i className="material-icons-outlined">east</i>
                </button>
              </div>
            </div>
            <div className="col-sm-12 col-md-4">
              <Card style={{ padding: 18 }}>
                <div className={styles.quizSidebarProgress}>
                  <div className={styles.quizSidebarProgressRow}>
                    <span>Questões realizadas:</span>
                    <span className={styles.quizSidebarProgressCount}>
                      {answers.filter((item) => item.answer !== '').length} de{' '}
                      {answers.length}
                    </span>
                  </div>
                  <div className={styles.quizProgressWrap}>
                    <div
                      className={styles.quizProgressFill}
                      style={{
                        width: `${
                          (answers.filter((item) => item.answer !== '').length /
                            answers.length) *
                          100
                        }%`,
                      }}
                    />
                  </div>
                </div>

                <div className={styles.quizLegend}>
                  <div className={styles.quizLegendItem}>
                    <span
                      className={`${styles.quizLegendDot} ${styles.quizLegendDotDone}`}
                    />
                    Respondido
                  </div>
                  <div className={styles.quizLegendItem}>
                    <span className={styles.quizLegendDot} />
                    Sem resposta
                  </div>
                </div>

                <div className={styles.quizNums}>
                  {answers.map((answer) => {
                    const done = answer.answer !== '';
                    const current = answer.questionIndex === page;
                    return (
                      <button
                        key={answer.questionIndex}
                        type="button"
                        className={`${styles.quizNum} ${
                          current
                            ? styles.quizNumCurrent
                            : done
                              ? styles.quizNumDone
                              : ''
                        }`}
                        onClick={() => goToQuestion(answer.questionIndex)}
                      >
                        {answer.questionIndex + 1}
                        {done && !current && (
                          <span className={styles.quizNumDot} />
                        )}
                      </button>
                    );
                  })}
                </div>

                <button
                  type="button"
                  className={styles.quizSidebarCta}
                  onClick={async () => {
                    const updatedAnswers = handleSubmit();
                    if (
                      page === attempt.questions?.length - 1 ||
                      answers.filter((item) => item.answer === '').length === 0
                    ) {
                      handleFinish(updatedAnswers);
                    } else {
                      setPage(page + 1);
                      setChosenAnswer(
                        answers[page + 1].answer ? answers[page + 1].answer : ''
                      );
                    }
                  }}
                >
                  {page === attempt.questions?.length - 1 ||
                  answers.filter((item) => item.answer === '').length === 0
                    ? 'Finalizar'
                    : 'Continuar'}
                  <i className="material-icons-outlined">east</i>
                </button>

                {countDownVisible && (
                  <div className={styles.quizTimer}>
                    <p className={styles.quizTimerLabel}>Tempo restante:</p>
                    <div
                      className={`${styles.quizTimerValue} ${
                        seconds < 20 ? styles['blink-red'] : ''
                      }`}
                    >
                      <i className="material-icons-outlined">schedule</i>
                      {formatarTempo(seconds)}
                    </div>
                  </div>
                )}
              </Card>
            </div>
          </div>
        </div>
      )}

      {finishedQuiz &&
        (() => {
          const coinName = client?.coinName
            ? client.coinName.replace(/\((\w+)\)/, (_, conteudo) => conteudo)
            : 'pontos';
          const showVerdict = !isKnowledgeTrail;
          const resultPassed = alreadyScoredPreviously ? true : totalPoints > 0;
          const resultTone = !showVerdict
            ? styles.quizResultNeutral
            : resultPassed
              ? styles.quizResultSuccess
              : styles.quizResultDanger;
          const resultTextTone = !showVerdict
            ? styles.quizResultTextNeutral
            : resultPassed
              ? styles.quizResultTextSuccess
              : styles.quizResultTextDanger;
          const hasMoreAttempts =
            !amountOfRetries || nOfAttemps < amountOfRetries;
          const tryAgain = () =>
            returnPath
              ? navigate(returnPath, { state: returnState })
              : navigate(`${ACTION_DETAILS}/${actionId}`);

          return (
            <div className="container">
              <div className="mb-3">
                <ButtonBack title="Quiz" />
              </div>
              <div className="row g-3">
                <div className="col-sm-12 col-md-8">
                  <Card style={{ padding: 0, overflow: 'hidden' }}>
                    <div className={styles.quizResultHeader}>
                      <i className="material-icons-outlined">article</i>
                      <h2>Resultado do Quiz</h2>
                    </div>
                    <div className={styles.quizResultBody}>
                      <div
                        className={`${styles.quizResultIconCircle} ${resultTone}`}
                      >
                        <i className="material-icons-outlined">
                          {!showVerdict
                            ? 'fact_check'
                            : resultPassed
                              ? 'emoji_events'
                              : 'sentiment_dissatisfied'}
                        </i>
                      </div>
                      <p
                        className={`${styles.quizResultPct} ${resultTextTone}`}
                      >
                        {accuracyPercentage}% de acertos
                      </p>
                      <p className={styles.quizResultDetail}>
                        {amountOfCorrectAnswers} de {attempt.questions.length}{' '}
                        questões corretas
                      </p>
                      <p
                        className={`${styles.quizResultStatus} ${resultTextTone}`}
                      >
                        {!showVerdict
                          ? 'Resultado registrado. Seu desempenho conta para a média da trilha.'
                          : resultPassed
                            ? '✓ Parabéns! Você pontuou neste quiz.'
                            : '✗ Você não pontuou nesta tentativa.'}
                      </p>
                      <div className={styles.quizResultBarWrap}>
                        <div className={styles.quizProgressWrap}>
                          <div
                            className={`${styles.quizProgressFill} ${
                              !showVerdict
                                ? styles.quizResultBarNeutral
                                : resultPassed
                                  ? styles.quizResultBarSuccess
                                  : styles.quizResultBarDanger
                            }`}
                            style={{ width: `${accuracyPercentage}%` }}
                          />
                        </div>
                      </div>
                      <div className={styles.quizResultActions}>
                        {hasMoreAttempts && (
                          <button
                            type="button"
                            className={styles.quizResultRetry}
                            onClick={tryAgain}
                          >
                            <i className="material-icons-outlined">replay</i>
                            Tentar novamente
                          </button>
                        )}
                        <button
                          type="button"
                          className={styles.quizResultCta}
                          onClick={() =>
                            returnPath
                              ? navigate(returnPath, { state: returnState })
                              : navigate(`${HOME}`)
                          }
                        >
                          <i className="material-icons-outlined">check</i>
                          Finalizar
                        </button>
                      </div>
                    </div>
                  </Card>
                </div>
                {!isKnowledgeTrail && (
                  <div className="col-sm-12 col-md-4">
                    <Card style={{ padding: 18 }}>
                      <div className={styles.quizPointsHeader}>
                        <i className="material-icons-outlined">emoji_events</i>
                        <h3>Pontuação</h3>
                      </div>
                      <p className={styles.quizPointsLabel}>Este quiz vale:</p>
                      <div className={styles.quizPointsValueBox}>
                        {`${quizPoints} ${coinName}`}
                      </div>
                      {alreadyScoredPreviously ? (
                        <>
                          <p className={styles.quizPointsLabel}>
                            {`Você já recebeu ${previousScoredPoints} ${coinName} neste quiz:`}
                          </p>
                          <div
                            className={`${styles.quizScoreBox} ${styles.quizScoreBoxSuccess}`}
                          >
                            {previousScoredAt
                              ? moment(previousScoredAt).format('DD/MM/YYYY')
                              : ''}
                          </div>
                        </>
                      ) : (
                        <>
                          <p className={styles.quizPointsLabel}>
                            Você pontuou até agora:
                          </p>
                          <div
                            className={`${styles.quizScoreBox} ${
                              totalPoints > 0
                                ? styles.quizScoreBoxSuccess
                                : styles.quizScoreBoxNeutral
                            }`}
                          >
                            {`${totalPoints} ${coinName}`}
                          </div>
                        </>
                      )}

                      {!resultPassed && (
                        <div className={styles.quizTipsCard}>
                          <p className={styles.quizTipsTitle}>
                            💡 Dicas para melhorar:
                          </p>
                          <ul className={styles.quizTipsList}>
                            <li>Revise o conteúdo com atenção</li>
                            <li>Leia o material de apoio disponível</li>
                            <li>
                              Anote os pontos principais antes de responder
                            </li>
                          </ul>
                        </div>
                      )}
                    </Card>
                  </div>
                )}
              </div>
            </div>
          );
        })()}

      <ModalHasAnswered
        modalIsOpen={modalHasAnsweredIsOpen}
        toggleModal={() => setModalHasAnsweredIsOpen(false)}
        cancelAnswer={() =>
          returnPath
            ? navigate(returnPath, { state: returnState })
            : navigate(`${ACTION_DETAILS}/${actionId}`)
        }
      />
      <ModalHasSucced
        modalIsOpen={modalHasSucced}
        toggleModal={() => setModalHasSucced(false)}
        earnedPoints={totalPoints}
        correctAnswers={amountOfCorrectAnswers}
        accuracyPercentage={accuracyPercentage}
      />
      <ModalHasFailed
        modalIsOpen={modalHasFailed}
        toggleModal={() => setModalHasFailed(false)}
        accuracyPercentage={accuracyPercentage}
        minimumScore={minimumScore}
        cancelAnswer={() =>
          returnPath
            ? navigate(returnPath, { state: returnState })
            : navigate(`${HOME}`)
        }
        tryAgain={() =>
          returnPath
            ? navigate(returnPath, { state: returnState })
            : navigate(`${ACTION_DETAILS}/${actionId}`)
        }
      />
      <ModalNoMoreAttempts
        modalIsOpen={modalNoMoreAttempts}
        toggleModal={() => setModalNoMoreAttempts(false)}
      />
      <ModalHasEmptyAnswers
        modalIsOpen={modalHasEmptyAnswers}
        toggleModal={() => setModalHasEmptyAnswers(false)}
        emptyAnswersCount={emptyAnswersCount}
        finishQuiz={async () => {
          await sendAwsers(answers);
          setFinishedQuiz(true);
        }}
      />
      <ModalPerformanceSucced
        modalIsOpen={modalPerformanceSucced}
        toggleModal={() => setModalPerformanceSucced(false)}
        performance={accuracyPercentage}
      />
      <ModalPerformanceFailed
        modalIsOpen={modalPerformanceFailed}
        toggleModal={() => setModalPerformanceFailed(false)}
        performance={accuracyPercentage}
        minimumScore={minimumScore}
        tryAgain={() =>
          returnPath
            ? navigate(returnPath, { state: returnState })
            : navigate(`${ACTION_DETAILS}/${actionId}`)
        }
      />
    </>
  );
}
